import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  addDoc,
  deleteDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  doc,
  where,
  Timestamp,
  query,
  orderBy
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/backend/lib/firebase';
import { Booking, GuestInfo } from '@/shared/types';
import type { Contract } from '@/shared/types';
import { createStudentAccount } from '@/backend/services/students';
import { generateSecurePassword } from '@/backend/utils/passwordGenerator';

export interface StudentCredential {
  name: string;
  email: string;
  password: string;
}

/**
 * Creates one student account per guest of a booking and returns the generated
 * credentials plus the resulting student IDs. Shared by the guest-approval flow
 * and the admin offline-booking flow.
 */
async function createStudentAccountsForBooking(
  booking: Pick<Booking, 'id' | 'residenceId' | 'guests'>
): Promise<{ credentials: StudentCredential[]; studentIds: string[] }> {
  const credentials: StudentCredential[] = [];
  const studentIds: string[] = [];

  for (const guest of booking.guests) {
    const password = generateSecurePassword(16);
    const studentId = await createStudentAccount(
      guest.email,
      password,
      guest.name,
      guest.phone,
      booking.residenceId,
      booking.id
    );
    studentIds.push(studentId);
    credentials.push({ name: guest.name, email: guest.email, password });
  }

  return { credentials, studentIds };
}

export const useAdminBookings = () => {
  return useQuery({
    queryKey: ['admin', 'bookings'],
    queryFn: async () => {
      const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        // Normalize legacy bookings that used guestName/guestEmail/guestPhone
        if (!data.guests) {
          data.guests = [{ name: data.guestName ?? '', email: data.guestEmail ?? '', phone: data.guestPhone ?? '' }];
        }
        return { id: doc.id, ...data };
      }) as Booking[];
    }
  });
};

export const useUpdateBookingStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      rejectionComment
    }: {
      id: string;
      status: 'pending' | 'approved' | 'rejected';
      rejectionComment?: string;
    }) => {
      await updateDoc(doc(db, 'bookings', id), {
        status,
        ...(status === 'rejected' && rejectionComment ? { rejectionComment } : {}),
        updatedAt: Timestamp.now()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'bookings'] });
    }
  });
};

export interface CancelApprovedBookingResult {
  success: boolean;
  deletedAccounts: number;
  keptAccounts: Array<{ studentId: string; reason: string }>;
}

/**
 * Cancel an APPROVED booking via the cancelApprovedBooking Cloud Function.
 * The function frees the unit and cleans up student accounts that are
 * exclusively tied to this booking (keeping any with contracts / other
 * bookings). Client can't delete Firebase Auth users, hence the callable.
 */
export const useCancelApprovedBooking = () => {
  const queryClient = useQueryClient();
  const fn = httpsCallable<{ bookingId: string; reason?: string }, CancelApprovedBookingResult>(
    getFunctions(undefined, 'europe-west1'),
    'cancelApprovedBooking'
  );

  return useMutation({
    mutationFn: async ({ bookingId, reason }: { bookingId: string; reason?: string }) => {
      const res = await fn({ bookingId, reason });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'bookings'] });
      queryClient.invalidateQueries({ queryKey: ['roomTypeOccupancy'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'students'] });
    },
  });
};

export interface BookingPasswordStatus {
  anyChanged: boolean;
  changedEmails: string[];
}

/**
 * Batched hook that returns, for each approved booking, whether any of its
 * students has already changed their temporary password. Used to gate the
 * "Resend credentials" admin action both on table rows and in the detail modal.
 */
export const useApprovedBookingsPasswordStatus = (bookings: Booking[]) => {
  const approved = bookings.filter(
    (b) => b.status === 'approved' && b.studentIds && b.studentIds.length > 0
  );
  const allIds = Array.from(
    new Set(approved.flatMap((b) => b.studentIds ?? []))
  ).sort();

  return useQuery({
    queryKey: ['admin', 'bookings', 'password-status-batch', allIds],
    enabled: allIds.length > 0,
    queryFn: async () => {
      const snaps = await Promise.all(allIds.map((id) => getDoc(doc(db, 'users', id))));
      const changedByStudentId = new Map<string, string>();
      snaps.forEach((snap, i) => {
        if (snap.exists() && snap.data()?.needsPasswordChange === false) {
          changedByStudentId.set(allIds[i], snap.data()?.email as string);
        }
      });

      const result = new Map<string, BookingPasswordStatus>();
      for (const b of approved) {
        const changedEmails = (b.studentIds ?? [])
          .map((id) => changedByStudentId.get(id))
          .filter((email): email is string => Boolean(email));
        result.set(b.id, { anyChanged: changedEmails.length > 0, changedEmails });
      }
      return result;
    },
  });
};

/**
 * Since pending bookings no longer block availability, overlapping pendings on
 * the same unit are normal — approval is the choke point that resolves them.
 * Returns the unit (roomTypeId) this booking should occupy: the assigned unit
 * if still free, otherwise the first free sibling unit of the same typology.
 * Throws only when NO unit of the typology is free for the booking's
 * (whole-month) range.
 */
async function resolveUnitForBooking(booking: Booking): Promise<string> {
  if (!booking.roomTypeId) throw new Error('This booking has no room assigned.');
  const checkIn = booking.checkIn.toDate();
  const checkOut = booking.checkOut.toDate();

  // Sibling units of the same typology; the originally assigned unit first.
  let unitIds = [booking.roomTypeId];
  const rtSnap = await getDoc(doc(db, 'roomTypes', booking.roomTypeId));
  const rt = rtSnap.exists() ? (rtSnap.data() as { residenceId?: string; typologyKey?: string }) : null;
  if (rt?.typologyKey && rt.residenceId) {
    const sibs = await getDocs(
      query(
        collection(db, 'roomTypes'),
        where('residenceId', '==', rt.residenceId),
        where('typologyKey', '==', rt.typologyKey)
      )
    );
    const ids = sibs.docs.map(d => d.id).sort();
    unitIds = [booking.roomTypeId, ...ids.filter(id => id !== booking.roomTypeId)];
  }

  // Occupied ranges per unit: approved/blocked bookings + contracts
  // (terminated contracts free dates after terminatedAt). Firestore 'in'
  // accepts max 30 values, so chunk.
  const occupied = new Map<string, Array<{ start: Date; end: Date }>>(unitIds.map(id => [id, []]));
  for (let i = 0; i < unitIds.length; i += 30) {
    const chunk = unitIds.slice(i, i + 30);
    const [bSnap, cSnap] = await Promise.all([
      getDocs(
        query(
          collection(db, 'bookings'),
          where('roomTypeId', 'in', chunk),
          where('status', 'in', ['approved', 'blocked'])
        )
      ),
      getDocs(query(collection(db, 'contracts'), where('roomTypeId', 'in', chunk))),
    ]);
    for (const d of bSnap.docs) {
      if (d.id === booking.id) continue;
      const b = d.data() as Booking;
      if (b.roomTypeId) {
        occupied.get(b.roomTypeId)?.push({ start: b.checkIn.toDate(), end: b.checkOut.toDate() });
      }
    }
    for (const d of cSnap.docs) {
      const c = d.data() as Contract;
      const cEnd =
        c.status === 'terminated' && c.terminatedAt ? c.terminatedAt.toDate() : c.endDate.toDate();
      occupied.get(c.roomTypeId)?.push({ start: c.startDate.toDate(), end: cEnd });
    }
  }

  for (const id of unitIds) {
    const hasConflict = occupied.get(id)!.some(r => checkIn < r.end && checkOut > r.start);
    if (!hasConflict) return id;
  }
  throw new Error(
    'No unit of this typology is free for these dates. Reject the booking or free up a unit first.'
  );
}

/** True when `unitId` has an approved/blocked booking or contract overlapping the range. */
async function unitHasConflict(
  unitId: string,
  checkIn: Date,
  checkOut: Date,
  excludeBookingId?: string
): Promise<boolean> {
  const [bSnap, cSnap] = await Promise.all([
    getDocs(
      query(
        collection(db, 'bookings'),
        where('roomTypeId', '==', unitId),
        where('status', 'in', ['approved', 'blocked'])
      )
    ),
    getDocs(query(collection(db, 'contracts'), where('roomTypeId', '==', unitId))),
  ]);
  for (const d of bSnap.docs) {
    if (d.id === excludeBookingId) continue;
    const b = d.data() as Booking;
    if (checkIn < b.checkOut.toDate() && checkOut > b.checkIn.toDate()) return true;
  }
  for (const d of cSnap.docs) {
    const c = d.data() as Contract;
    const cEnd =
      c.status === 'terminated' && c.terminatedAt ? c.terminatedAt.toDate() : c.endDate.toDate();
    if (checkIn < cEnd && checkOut > c.startDate.toDate()) return true;
  }
  return false;
}

/**
 * Move a booking to another unit of the same typology. Refuses when the
 * target unit already has an overlapping approved/blocked booking or contract.
 */
export const useMoveBookingUnit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ booking, targetRoomTypeId }: { booking: Booking; targetRoomTypeId: string }) => {
      if (targetRoomTypeId === booking.roomTypeId) return;
      const conflict = await unitHasConflict(
        targetRoomTypeId,
        booking.checkIn.toDate(),
        booking.checkOut.toDate(),
        booking.id
      );
      if (conflict) {
        throw new Error('That unit is already taken for these dates. Pick another one.');
      }
      await updateDoc(doc(db, 'bookings', booking.id), {
        roomTypeId: targetRoomTypeId,
        updatedAt: Timestamp.now(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'bookings'] });
      queryClient.invalidateQueries({ queryKey: ['roomTypeOccupancy'] });
    },
  });
};

/**
 * Hook to approve booking and create one student account per guest.
 * Returns an array of credentials for the admin to distribute.
 */
export const useApproveBookingWithStudent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ booking }: { booking: Booking }) => {
      // Resolve the unit FIRST — before account creation, so a losing approval
      // leaves no orphan student accounts behind. May reassign the booking to
      // a free sibling unit of the same typology.
      const resolvedRoomTypeId = await resolveUnitForBooking(booking);
      const reassigned = resolvedRoomTypeId !== booking.roomTypeId;

      const { credentials, studentIds } = await createStudentAccountsForBooking(booking);

      await updateDoc(doc(db, 'bookings', booking.id), {
        status: 'approved',
        studentIds,
        roomTypeId: resolvedRoomTypeId,
        updatedAt: Timestamp.now(),
      });

      return { credentials, reassigned };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'bookings'] });
      queryClient.invalidateQueries({ queryKey: ['my-contract'] });
      queryClient.invalidateQueries({ queryKey: ['my-consumption'] });
    },
  });
};

export interface CreateAdminBlockInput {
  residenceId: string;
  roomTypeId: string;
  checkIn: Date;
  checkOut: Date;
  adminNote?: string;
}

/**
 * Creates a pure availability block: a unit taken offline for a date range with
 * no guest. Stored as a `type: 'admin'`, `status: 'blocked'` booking so the
 * existing occupancy pipeline (getAvailableRooms, useRoomTypeOccupancy) treats
 * the unit as occupied.
 */
export const useCreateAdminBlock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAdminBlockInput) => {
      const note = data.adminNote?.trim();
      const docRef = await addDoc(collection(db, 'bookings'), {
        residenceId: data.residenceId,
        roomTypeId: data.roomTypeId,
        checkIn: Timestamp.fromDate(data.checkIn),
        checkOut: Timestamp.fromDate(data.checkOut),
        numberOfOccupants: 0,
        guests: [],
        notes: '',
        status: 'blocked',
        type: 'admin',
        ...(note ? { adminNote: note } : {}),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'bookings'] });
      queryClient.invalidateQueries({ queryKey: ['roomTypeOccupancy'] });
    },
  });
};

export interface CreateAdminOfflineBookingInput {
  residenceId: string;
  roomTypeId: string;
  checkIn: Date;
  checkOut: Date;
  numberOfOccupants: number;
  guests: GuestInfo[];
  adminNote?: string;
}

/**
 * Creates an offline booking on behalf of a guest who booked by phone/in person.
 * Auto-approved on creation: it creates one student account per guest and lands
 * as a `type: 'admin'`, `status: 'approved'` booking. Returns the generated
 * credentials so the admin can hand them out.
 */
export const useCreateAdminOfflineBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAdminOfflineBookingInput) => {
      const note = data.adminNote?.trim();

      // 1. Fail fast if any guest email already belongs to a user. This avoids
      //    creating a half-formed booking (or partial accounts) when creation
      //    would error on a duplicate email further down.
      for (const guest of data.guests) {
        const existing = await getDocs(
          query(collection(db, 'users'), where('email', '==', guest.email))
        );
        if (!existing.empty) {
          throw new Error(`A user with the email ${guest.email} already exists`);
        }
      }

      // 2. Pre-allocate the booking id so student records can reference it,
      //    WITHOUT writing the booking yet — nothing is persisted if step 3 fails.
      const bookingRef = doc(collection(db, 'bookings'));

      // 3. Create one student account per guest. If this throws, no booking exists.
      const { credentials, studentIds } = await createStudentAccountsForBooking({
        id: bookingRef.id,
        residenceId: data.residenceId,
        guests: data.guests,
      });

      // 4. Persist the booking as already-approved now that accounts exist.
      await setDoc(bookingRef, {
        residenceId: data.residenceId,
        roomTypeId: data.roomTypeId,
        checkIn: Timestamp.fromDate(data.checkIn),
        checkOut: Timestamp.fromDate(data.checkOut),
        numberOfOccupants: data.numberOfOccupants,
        guests: data.guests,
        notes: '',
        status: 'approved',
        type: 'admin',
        studentIds,
        ...(note ? { adminNote: note } : {}),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      return { id: bookingRef.id, credentials };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'bookings'] });
      queryClient.invalidateQueries({ queryKey: ['my-contract'] });
      queryClient.invalidateQueries({ queryKey: ['my-consumption'] });
      queryClient.invalidateQueries({ queryKey: ['roomTypeOccupancy'] });
    },
  });
};

export interface UpdateAdminBookingInput {
  id: string;
  residenceId: string;
  roomTypeId: string;
  checkIn: Date;
  checkOut: Date;
  adminNote?: string;
}

/**
 * Edits an admin-created entry (block or offline booking): residence, unit,
 * dates and note. Guest details / student accounts are intentionally left
 * untouched — only the booking document fields change.
 */
export const useUpdateAdminBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateAdminBookingInput) => {
      await updateDoc(doc(db, 'bookings', data.id), {
        residenceId: data.residenceId,
        roomTypeId: data.roomTypeId,
        checkIn: Timestamp.fromDate(data.checkIn),
        checkOut: Timestamp.fromDate(data.checkOut),
        adminNote: data.adminNote?.trim() ?? '',
        updatedAt: Timestamp.now(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'bookings'] });
      queryClient.invalidateQueries({ queryKey: ['roomTypeOccupancy'] });
    },
  });
};

/**
 * Deletes an admin-created entry (block or offline booking). Used to lift a
 * block once the unit is available again. Guarded in the UI to admin entries.
 */
export const useDeleteAdminBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, 'bookings', id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'bookings'] });
      queryClient.invalidateQueries({ queryKey: ['roomTypeOccupancy'] });
    },
  });
};
